import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { downscaleImage, scanLabel } from './labelScan';

export { mergeScanValues } from './labelScan';

const FIELD_ORDER = {
  wine: ['name', 'producer', 'vintage', 'abv', 'volume_ml', 'classification', 'wine_type', 'production_area'],
  beer: ['name', 'brewery', 'style', 'abv', 'volume_ml', 'ibu'],
};
const ASSIGN_TARGETS = { wine: ['name', 'producer'], beer: ['name', 'brewery'] };
const CODED_FIELDS = ['classification', 'wine_type'];

// Photo of a wine or beer label -> data read by the server (local OCR). The result is only a hint:
// the parent form receives the reliable fields through onApply and the user reviews everything before saving.
// Props: kind 'wine' | 'beer'; onApply(values, scan, options); onPhoto(file) with the downscaled JPEG.
function LabelScanner({ kind = 'wine', onApply, onPhoto }) {
  const { t, tc } = useI18n();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState('name');
  const [assigned, setAssigned] = useState(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const run = async (event) => {
    const original = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!original) return;
    setBusy(true); setError(null); setScan(null); setAssigned(null); setTarget('name');
    const file = await downscaleImage(original);
    setPreview(URL.createObjectURL(file));
    if (onPhoto) onPhoto(file);
    try {
      const result = await scanLabel(file, kind);
      const values = {};
      Object.entries(result.fields || {}).forEach(([name, field]) => { if (field.autofill) values[name] = field.value; });
      setScan(result);
      if (onApply && Object.keys(values).length > 0) onApply(values, result, { force: false });
    } catch (failure) {
      const key = failure && failure.code ? `api.${failure.code}` : null;
      const known = key && t(key) !== key ? t(key) : null;
      setError(known || (failure && failure.detail) || t('labels.error_generic'));
    }
    setBusy(false);
  };

  const assign = (text) => {
    const field = target;
    if (onApply) onApply({ [field]: text }, scan, { force: true });
    setAssigned({ field, text });
    const next = ASSIGN_TARGETS[kind].find((x) => x !== field);
    if (next) setTarget(next);
  };

  const show = (field, value) => (CODED_FIELDS.includes(field) ? tc(field, value) : String(value));
  const entries = scan ? FIELD_ORDER[kind].filter((f) => scan.fields && scan.fields[f]) : [];
  const filled = entries.filter((f) => scan.fields[f].autofill).length;
  const notFilled = entries.length - filled;

  return (
    <div className="label-scanner">
      <div className="label-scanner-actions">
        <button type="button" className="reset-btn" disabled={busy} onClick={() => cameraRef.current && cameraRef.current.click()}>
          📷 {t('labels.take_photo')}
        </button>
        <button type="button" className="reset-btn" disabled={busy} onClick={() => galleryRef.current && galleryRef.current.click()}>
          {t('labels.choose_photo')}
        </button>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={run} style={{ display: 'none' }} />
        <input ref={galleryRef} type="file" accept="image/*" onChange={run} style={{ display: 'none' }} />
      </div>
      <small className="import-hint">{t(`labels.hint_${kind}`)}</small>

      {busy && <div className="label-scanner-busy" role="status"><span className="label-spinner" aria-hidden="true" /> {t('labels.reading')}</div>}
      {error && <div className="message error" style={{ marginTop: 8 }}>{error}</div>}

      {scan && (
        <div className="label-scanner-result">
          {preview && <img className="label-scanner-preview" src={preview} alt={t('labels.photo_alt')} />}
          <div className="label-scanner-body">
            <strong>{t('labels.detected')}</strong>
            {entries.length === 0 && <p className="empty">{t('labels.nothing_found')}</p>}
            <ul className="label-fields">
              {entries.map((field) => {
                const item = scan.fields[field];
                return (
                  <li key={field}>
                    <span className="label-field-name">{t(`labels.field.${field}`)}</span>
                    <span className="label-field-value">{show(field, item.value)}</span>
                    <span className={`label-badge ${item.confidence}`}>{t(`labels.confidence.${item.confidence}`)}</span>
                    {!item.autofill && <small className="label-not-filled">{t('labels.not_filled')}</small>}
                  </li>
                );
              })}
            </ul>
            {entries.length > 0 && <small>{t('labels.filled_summary', { n: filled })}{notFilled > 0 ? ` ${t('labels.not_filled_summary', { n: notFilled })}` : ''}</small>}
            <div className="label-review">{t('labels.review_notice')}</div>

            {scan.suggestions && scan.suggestions.length > 0 && (
              <div className="label-suggestions">
                <div>{t('labels.suggestions_title')}</div>
                <div className="chip-group">
                  {ASSIGN_TARGETS[kind].map((f) => (
                    <button key={f} type="button" className={`chip${target === f ? ' selected' : ''}`} onClick={() => setTarget(f)}>
                      {t(`labels.assign_to.${f}`)}
                    </button>
                  ))}
                </div>
                <div className="chip-group">
                  {scan.suggestions.map((s) => (
                    <button key={s.text} type="button" className="chip" onClick={() => assign(s.text)} title={t(`labels.confidence.${s.confidence}`)}>
                      {s.text}
                    </button>
                  ))}
                </div>
                {assigned && <small>{t('labels.assigned', { field: t(`labels.field.${assigned.field}`), text: assigned.text })}</small>}
              </div>
            )}

            {scan.text && (
              <details className="label-raw">
                <summary>{t('labels.raw_text')}</summary>
                <pre>{scan.text}</pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LabelScanner;
